import React, { useEffect, useState } from "react";
import { Alert, Card, Button, Badge, Avatar, IconBtn, Input, Tabs, Field, Spinner } from "../../design-system";
import { Icon, SupHeader, SupSection, SupSelect, SupKpi, SupEmpty, StatusBadge } from "./atoms";
import { RES_TYPE } from "./data";
import { useSoporteNav } from "./nav";
import {
  getSupportResources, createSupportResource, getSupportResource, deleteSupportResource,
  setSupportResourceStatus, grantSupportResourceAccess, revokeSupportResourceAccess,
  addSupportResourceDocument, removeSupportResourceDocument, getEmployees,
} from "../../helpers/kronaly_helper";

const unwrap = (r) => r?.data ?? r;
const empName = (e) => [e?.first_name, e?.last_name].filter(Boolean).join(" ").trim() || "Sin nombre";

const RES_STATES = [
  { id: "operativo", label: "Operativo", tone: "success" },
  { id: "incidencia", label: "Con incidencia", tone: "warning" },
  { id: "mantenimiento", label: "En mantenimiento", tone: "pending" },
  { id: "retirado", label: "Retirado", tone: "neutral" },
];
const stateMeta = (id) => RES_STATES.find((s) => s.id === id) || RES_STATES[0];
const typeMeta = (t) => RES_TYPE[t] || { label: t || "—", icon: "cloud" };

function ResIcon({ type, size = 40 }) {
  const m = typeMeta(type);
  return (
    <span style={{ width: size, height: size, borderRadius: 10, background: "var(--kr-neutral-100)", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "var(--kr-fg-muted)", flexShrink: 0 }}>
      <Icon name={m.icon} size={Math.round(size * 0.5)} />
    </span>
  );
}

function ResStatusBadge({ status }) {
  const m = stateMeta(status);
  return <Badge tone={m.tone} dot>{m.label}</Badge>;
}

/**
 * Soporte IT · Recursos IT — catálogo de software, SaaS, sistemas internos,
 * licencias y cuentas (lo no físico). Detecta incidencias recurrentes por
 * aplicación. Cableado contra /support/resources.
 */
function SoporteRecursos() {
  document.title = "Recursos IT · Soporte IT | Kronaly";
  const nav = useSoporteNav();
  const [q, setQ] = useState("");
  const [fType, setFType] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    const params = {};
    if (fType) params.type = fType;
    if (q.trim()) params.q = q.trim();
    return getSupportResources(params)
      .then((r) => { setRows(unwrap(r) || []); setError(null); })
      .catch((e) => setError(e?.message || String(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let alive = true;
    const t = setTimeout(() => { if (alive) load(); }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [q, fType]);

  const conIncidencia = rows.filter((r) => r.status === "incidencia").length;
  const saas = rows.filter((r) => r.type === "saas" || r.type === "licencia").length;
  const internos = rows.filter((r) => r.type === "interno" || r.type === "servidor").length;

  return (
    <div className="kr-page">
      <SupHeader
        eyebrow="Soporte IT"
        title="Recursos IT"
        subtitle="Software, servicios SaaS, sistemas internos, licencias y cuentas. Detecta problemas recurrentes por aplicación."
        actions={<Button variant="primary" icon="Plus" onClick={() => setCreating(true)}>Añadir recurso</Button>}
      />

      {error && <Alert tone="danger" title="No se pudieron cargar los recursos">{error}</Alert>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }} data-responsive-grid="4">
        <SupKpi label="Recursos gestionados" value={rows.length} icon="cloud" />
        <SupKpi label="Con incidencia activa" value={conIncidencia} icon="alert" accent="var(--kr-warning-600)" />
        <SupKpi label="Licencias / SaaS" value={saas} icon="tag" />
        <SupKpi label="Sistemas internos" value={internos} icon="server" />
      </div>

      <Card padded={false}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: "1px solid var(--kr-border-subtle)" }}>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar recurso o proveedor…" icon="Search" style={{ flex: 1, maxWidth: 360 }} />
          <SupSelect value={fType} onChange={setFType} placeholder="Tipo de recurso" options={Object.keys(RES_TYPE).map((k) => ({ value: k, label: RES_TYPE[k].label }))} style={{ width: 200 }} />
        </div>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}><Spinner size={20} /></div>
        ) : rows.length === 0 ? (
          <SupEmpty icon="cloud" title="Sin recursos" hint="Añade el primer software, servicio o sistema interno." />
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, padding: 16 }} data-responsive-grid="3">
            {rows.map((r) => <ResourceCard key={r.id} r={r} onOpen={() => setSelected(r.id)} />)}
          </div>
        )}
      </Card>

      {selected && <ResourceDrawer resId={selected} onClose={() => setSelected(null)} onChanged={load} nav={nav} />}
      {creating && <ResourceForm onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function ResourceCard({ r, onOpen }) {
  const m = typeMeta(r.type);
  return (
    <div onClick={onOpen} style={{ border: "1px solid var(--kr-border)", borderRadius: 12, padding: 16, background: "#fff", cursor: "pointer", boxShadow: "var(--kr-shadow-xs)", transition: "box-shadow .15s" }}
      onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "var(--kr-shadow-md)")} onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "var(--kr-shadow-xs)")}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <ResIcon type={r.type} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--kr-fg-strong)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.name}</div>
          <div style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>{m.label}{r.provider ? ` · ${r.provider}` : ""}</div>
        </div>
        <ResStatusBadge status={r.status} />
      </div>
      {r.description && <p style={{ fontSize: 12, color: "var(--kr-fg-muted)", margin: "12px 0 14px", lineHeight: 1.5, minHeight: 36 }}>{r.description}</p>}
      <div style={{ display: "flex", alignItems: "center", gap: 16, paddingTop: 12, borderTop: "1px solid var(--kr-border-subtle)" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--kr-fg-primary)" }}><Icon name="users" size={13} stroke="var(--kr-fg-muted)" /><strong>{r.users ?? 0}</strong> usuarios</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: r.open_tickets ? "var(--kr-warning-700)" : "var(--kr-fg-muted)" }}><Icon name="ticket" size={13} stroke="currentColor" /><strong>{r.open_tickets ?? 0}</strong> abiertos</span>
      </div>
    </div>
  );
}

// =====================================================================
//  Alta de recurso
// =====================================================================
function ResourceForm({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [provider, setProvider] = useState("");
  const [estado, setEstado] = useState("operativo");
  const [desc, setDesc] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const canSubmit = name.trim() && type && !busy;

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      await createSupportResource({
        name: name.trim(), type, provider: provider.trim() || null, status: estado,
        description: desc.trim() || null, notes: note.trim() || null,
      });
      onCreated();
    } catch (e) { setError(e?.message || String(e)); setBusy(false); }
  };

  return (
    <DrawerShell title="Nuevo recurso IT" eyebrow="Soporte IT · Recursos" icon={type ? typeMeta(type).icon : "cloud"} heading={name.trim() || "Nuevo recurso IT"} onClose={onClose}
      footer={<>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button variant="primary" icon="Check" disabled={!canSubmit} onClick={submit}>Crear recurso</Button>
      </>}>
      {error && <Alert tone="danger" title="No se pudo crear">{error}</Alert>}
      <Card>
        <SupSection eyebrow="Identificación" title="Datos del recurso" />
        <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 16 }}>
          <Field label="Nombre del recurso" required>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej. Microsoft 365, ERP Kronaly, VPN corporativa…" />
          </Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <Field label="Tipo de recurso" required>
              <SupSelect value={type} onChange={setType} placeholder="Selecciona…" options={Object.keys(RES_TYPE).map((k) => ({ value: k, label: RES_TYPE[k].label }))} />
            </Field>
            <Field label="Proveedor">
              <Input value={provider} onChange={(e) => setProvider(e.target.value)} placeholder="Microsoft, Adobe, Interno…" />
            </Field>
          </div>
          <Field label="Estado">
            <div style={{ display: "flex", gap: 6 }}>
              {RES_STATES.map((s) => (
                <button key={s.id} onClick={() => setEstado(s.id)} style={{
                  flex: 1, height: 36, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer",
                  border: `1px solid ${estado === s.id ? "var(--kr-action)" : "var(--kr-border)"}`,
                  background: estado === s.id ? "var(--kr-action-soft-bg)" : "#fff",
                  color: estado === s.id ? "var(--kr-action-soft-fg)" : "var(--kr-fg-muted)",
                }}>{s.label}</button>
              ))}
            </div>
          </Field>
        </div>
      </Card>
      <Card>
        <SupSection eyebrow="Detalle" title="Descripción" />
        <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} placeholder="Para qué sirve el recurso y a quién da servicio…"
          style={textareaStyle} />
      </Card>
      <Card>
        <SupSection eyebrow="Solo soporte" title="Notas internas" right={<Badge tone="neutral" variant="soft"><Icon name="lock" size={11} style={{ marginRight: 4 }} />Privado</Badge>} />
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Credenciales de administración, contrato, renovación…" style={textareaStyle} />
      </Card>
      <p style={{ fontSize: 12, color: "var(--kr-fg-muted)", margin: 0 }}>Los usuarios con acceso y la documentación se gestionan al abrir el recurso.</p>
    </DrawerShell>
  );
}

// =====================================================================
//  Detalle de recurso
// =====================================================================
function ResourceDrawer({ resId, onClose, onChanged, nav }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("tickets");
  const [busy, setBusy] = useState(false);

  const refetch = () => {
    setLoading(true);
    return getSupportResource(resId)
      .then((r) => { setData(unwrap(r)); setError(null); })
      .catch((e) => setError(e?.message || String(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { refetch(); }, [resId]); // eslint-disable-line

  const changeStatus = async (status) => {
    setBusy(true);
    try { await setSupportResourceStatus(resId, status); await refetch(); onChanged?.(); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("¿Eliminar este recurso? Sus tickets se conservan (sin recurso asociado).")) return;
    setBusy(true);
    try { await deleteSupportResource(resId); onChanged?.(); onClose(); }
    catch (e) { setError(e?.message || String(e)); setBusy(false); }
  };

  const m = data ? typeMeta(data.type) : null;

  return (
    <DrawerShell wide onClose={onClose}
      header={data && (
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <ResIcon type={data.type} size={48} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <h2 style={{ margin: 0, fontFamily: "var(--kr-font-display)", fontSize: 22, fontWeight: 700, letterSpacing: "-0.02em", color: "var(--kr-fg-strong)" }}>{data.name}</h2>
              <ResStatusBadge status={data.status} />
            </div>
            <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginTop: 4 }}>{m.label}{data.provider ? ` · ${data.provider}` : ""} · {data.access?.length || 0} con acceso</div>
          </div>
          <SupSelect value={data.status} onChange={changeStatus} options={RES_STATES.map((s) => ({ value: s.id, label: s.label }))} style={{ width: 170 }} />
          <IconBtn icon="Trash2" title="Eliminar recurso" onClick={remove} />
        </div>
      )}>
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}><Spinner size={20} /></div>
      ) : error ? (
        <Alert tone="danger" title="No se pudo cargar">{error}</Alert>
      ) : data && (
        <>
          {data.description && <p style={{ fontSize: 13, color: "var(--kr-fg-primary)", lineHeight: 1.55, margin: "0 0 14px" }}>{data.description}</p>}
          <div style={{ marginBottom: 14 }}>
            <Tabs value={tab} onChange={setTab} tabs={[
              { id: "tickets", label: "Tickets", count: data.tickets?.length || 0 },
              { id: "usuarios", label: "Usuarios", count: data.access?.length || 0 },
              { id: "doc", label: "Documentación", count: data.documents?.length || 0 },
            ]} />
          </div>

          {tab === "tickets" && <TicketsTab data={data} nav={nav} onClose={onClose} />}
          {tab === "usuarios" && <UsuariosTab resId={resId} data={data} busy={busy} onChanged={async () => { await refetch(); onChanged?.(); }} />}
          {tab === "doc" && <DocumentosTab resId={resId} data={data} onChanged={refetch} />}
        </>
      )}
    </DrawerShell>
  );
}

function TicketsTab({ data, nav, onClose }) {
  return (
    <Card padded={false}>
      <div style={{ padding: "14px 18px" }}><SupSection eyebrow="Histórico por servicio" title="Tickets de este recurso" /></div>
      {data.tickets?.length ? data.tickets.map((tk) => (
        <div key={tk.id} onClick={() => { onClose(); nav.go("ticket", { code: tk.id }); }} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 18px", borderTop: "1px solid var(--kr-border-subtle)", cursor: "pointer" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: "var(--kr-fg-strong)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{tk.title}</div>
            <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", fontFamily: "var(--kr-font-mono)" }}>{tk.number}</div>
          </div>
          <StatusBadge status={tk.status} />
        </div>
      )) : <div style={{ padding: 18, fontSize: 13, color: "var(--kr-fg-muted)" }}>Sin tickets asociados.</div>}
    </Card>
  );
}

function UsuariosTab({ resId, data, onChanged, busy }) {
  const [employees, setEmployees] = useState([]);
  const [add, setAdd] = useState("");
  const [working, setWorking] = useState(false);

  useEffect(() => {
    let alive = true;
    getEmployees().then((r) => { if (alive) setEmployees(unwrap(r) || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const has = new Set((data.access || []).map((a) => a.employee_profile_id));
  const available = employees.filter((e) => !has.has(e.id));

  const grant = async (employeeProfileId) => {
    if (!employeeProfileId) return;
    setWorking(true);
    try { await grantSupportResourceAccess(resId, [employeeProfileId]); setAdd(""); await onChanged(); }
    finally { setWorking(false); }
  };
  const revoke = async (employeeProfileId) => {
    setWorking(true);
    try { await revokeSupportResourceAccess(resId, employeeProfileId); await onChanged(); }
    finally { setWorking(false); }
  };

  return (
    <Card padded={false}>
      <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--kr-border-subtle)" }}>
        <SupSection eyebrow="Acceso" title={`${data.access?.length || 0} usuarios con acceso`} />
        <div style={{ marginTop: 12 }}>
          <SupSelect value={add} onChange={grant} placeholder={working || busy ? "Guardando…" : "Añadir usuario con acceso…"}
            options={available.map((e) => ({ value: e.id, label: e.position ? `${empName(e)} · ${e.position}` : empName(e) }))} />
        </div>
      </div>
      {data.access?.length ? data.access.map((u) => (
        <div key={u.employee_profile_id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 18px", borderTop: "1px solid var(--kr-border-subtle)" }}>
          <Avatar name={u.name || "?"} size={30} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: "var(--kr-fg-strong)" }}>{u.name || "—"}</div>
            {u.position && <div style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>{u.position}</div>}
          </div>
          <IconBtn icon="X" title="Quitar acceso" onClick={() => revoke(u.employee_profile_id)} />
        </div>
      )) : <div style={{ padding: 18, fontSize: 13, color: "var(--kr-fg-muted)" }}>Aún nadie con acceso registrado.</div>}
    </Card>
  );
}

function DocumentosTab({ resId, data, onChanged }) {
  const [title, setTitle] = useState("");
  const [docUrl, setDocUrl] = useState("");
  const [working, setWorking] = useState(false);

  const add = async () => {
    if (!title.trim()) return;
    setWorking(true);
    try { await addSupportResourceDocument(resId, { title: title.trim(), url: docUrl.trim() || null }); setTitle(""); setDocUrl(""); await onChanged(); }
    finally { setWorking(false); }
  };
  const remove = async (docId) => {
    setWorking(true);
    try { await removeSupportResourceDocument(resId, docId); await onChanged(); }
    finally { setWorking(false); }
  };

  return (
    <Card>
      <SupSection eyebrow="Documentación relacionada" title="Guías y enlaces" />
      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr auto", gap: 8, marginTop: 14 }}>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Título (ej. Cómo dar de alta un usuario)" icon="BookOpen" />
        <Input value={docUrl} onChange={(e) => setDocUrl(e.target.value)} placeholder="URL (opcional)" icon="Link" />
        <Button variant="secondary" icon="Plus" onClick={add} disabled={!title.trim() || working}>Añadir</Button>
      </div>
      {data.documents?.length ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 14 }}>
          {data.documents.map((d) => (
            <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", border: "1px solid var(--kr-border-subtle)", borderRadius: 8 }}>
              <Icon name="bookOpen" size={15} stroke="var(--kr-fg-muted)" />
              {d.url ? (
                <a href={d.url} target="_blank" rel="noreferrer" style={{ flex: 1, fontSize: 13, color: "var(--kr-action)", textDecoration: "none" }}>{d.title}</a>
              ) : (
                <span style={{ flex: 1, fontSize: 13, color: "var(--kr-fg-strong)" }}>{d.title}</span>
              )}
              <button onClick={() => remove(d.id)} style={{ border: "none", background: "transparent", color: "var(--kr-fg-muted)", cursor: "pointer", display: "inline-flex", padding: 0 }}><Icon name="x" size={14} /></button>
            </div>
          ))}
        </div>
      ) : <div style={{ fontSize: 13, color: "var(--kr-fg-muted)", marginTop: 14 }}>Sin documentación todavía.</div>}
    </Card>
  );
}

// =====================================================================
//  Shell de drawer reutilizable
// =====================================================================
function DrawerShell({ title, eyebrow, icon, heading, header, footer, wide, children, onClose }) {
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,19,22,0.45)", zIndex: 92 }} />
      <aside style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: wide ? 620 : 580, maxWidth: "100vw", background: "#fff", borderLeft: "1px solid var(--kr-border)", boxShadow: "var(--kr-shadow-lg)", zIndex: 93, display: "flex", flexDirection: "column" }} data-drawer>
        <div style={{ padding: "18px 24px", borderBottom: "1px solid var(--kr-border-subtle)", display: "flex", alignItems: "center", gap: 14 }}>
          {header ? <div style={{ flex: 1 }}>{header}</div> : (
            <>
              <span style={{ width: 44, height: 44, borderRadius: 10, background: "var(--kr-neutral-100)", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "var(--kr-fg-muted)", flexShrink: 0 }}>
                <Icon name={icon || "cloud"} size={22} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                {eyebrow && <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600 }}>{eyebrow}</div>}
                <h2 style={{ margin: "2px 0 0", fontFamily: "var(--kr-font-display)", fontSize: 20, fontWeight: 700, letterSpacing: "-0.02em", color: "var(--kr-fg-strong)" }}>{heading || title}</h2>
              </div>
            </>
          )}
          <IconBtn icon="X" onClick={onClose} title="Cerrar" />
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: 24, background: "var(--kr-neutral-50)", display: "flex", flexDirection: "column", gap: 16 }}>
          {children}
        </div>
        {footer && (
          <div style={{ padding: "14px 24px", borderTop: "1px solid var(--kr-border-subtle)", display: "flex", justifyContent: "flex-end", gap: 10, background: "#fff" }}>
            {footer}
          </div>
        )}
      </aside>
    </>
  );
}

const textareaStyle = { width: "100%", marginTop: 14, border: "1px solid var(--kr-border)", borderRadius: 6, padding: "10px 12px", fontSize: 13, fontFamily: "var(--kr-font-sans)", color: "var(--kr-fg-strong)", resize: "vertical", outline: "none", boxSizing: "border-box", lineHeight: 1.5 };

export default SoporteRecursos;
